import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, EmptyState, LoadingSkeleton, Modal } from "@a2z/ui";
import { endpoints } from "@/lib/api/endpoints";
import type { OlderChequesPage } from "@/lib/api/types";
import { useI18n, useTranslate } from "@/lib/i18n";
import { billPlaceLabel } from "@/lib/invoice/place";
import { queryKeys, useApiCursorQuery } from "@/shared/queries";
import { useErrorMessage } from "@/shared/errors";
import { useMoney } from "@/shared/ui";
import { useInvoiceDateFormat } from "@/modules/manage/lib/invoiceDisplay";

/**
 * THE FLOOR'S "SHOW OLDER" LIST (P5-Q, founder 2026-10-25).
 *
 * A CLOSED cheque nobody settled stays on its table — and a closed takeaway or
 * delivery on the Takeaway strip — for two days after its close. After that
 * the grid no longer carries it (it read every cheque the store ever left
 * unsettled, on every refresh of every device) and it is listed here instead,
 * newest close first, until somebody settles it. A row opens the bill, where
 * it settles like any other cheque.
 *
 * Read when the list OPENS, never on a floor refresh, so the floor pays
 * nothing for it; every open reads it afresh. Fifty at a time (P9.6, founder
 * 2026-10-31): «Show more» reads the next fifty after the last one shown, so
 * none is out of reach and none is listed twice. The money is still counted where
 * it always was — the drawer's cheques out, the Z — this moves it off the
 * floor only.
 */
export function OlderChequesButton({
  tableName,
}: {
  /** The floor's own table names, by id — the grid the button sits on. */
  tableName: (tableId: string) => string;
}) {
  const t = useTranslate();
  const [isOpen, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        title={t((m) => m.olderCheques.openHint)}
        onClick={() => setOpen(true)}
        data-testid="floor-older-cheques"
      >
        {t((m) => m.olderCheques.open)}
      </Button>
      {isOpen ? (
        <OlderChequesModal
          tableName={tableName}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function OlderChequesModal({
  tableName,
  onClose,
}: {
  tableName: (tableId: string) => string;
  onClose: () => void;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const money = useMoney();
  const dates = useInvoiceDateFormat();
  const errorMessage = useErrorMessage();
  const cheques = useApiCursorQuery<OlderChequesPage>(
    queryKeys.olderCheques,
    endpoints.restaurant.gridOlderCheques,
    { staleTime: 0 },
  );
  const rows = cheques.data?.pages.flatMap((page) => page.cheques) ?? [];

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title={t((m) => m.olderCheques.title)}
      closeLabel={t((m) => m.common.close)}
      data-testid="older-cheques-dialog"
    >
      <p className="pb-3 text-xs text-[color:var(--text-muted)]">
        {t((m) => m.olderCheques.note)}
      </p>
      {cheques.isLoading ? <LoadingSkeleton rows={3} /> : null}
      {cheques.isError ? (
        <p role="alert" className="text-xs text-[color:var(--danger)]">
          {errorMessage(cheques.error)}
        </p>
      ) : null}
      {cheques.data && rows.length === 0 ? (
        <EmptyState
          title={t((m) => m.olderCheques.emptyTitle)}
          description={t((m) => m.olderCheques.emptyBody)}
        />
      ) : null}
      {rows.length > 0 ? (
        <ul className="flex flex-col gap-2" data-testid="older-cheques-list">
          {rows.map((cheque) => (
            <li key={cheque.invoiceId}>
              <button
                type="button"
                onClick={() =>
                  navigate(`/${locale}/pos/invoices/${cheque.invoiceId}`)
                }
                data-testid={`older-cheque-${cheque.invoiceId}`}
                className="flex w-full items-center justify-between gap-3 rounded-[var(--radius-tile)] border border-[color:var(--border-light)] px-3 py-2.5 text-start transition-colors hover:bg-[color:var(--surface-alt)]"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-bold">
                    {/* `dir="ltr"` keeps "#12" from reading "12#". */}
                    <span dir="ltr" className="tabular-nums">
                      #{cheque.dailySequence}
                    </span>{" "}
                    · {billPlaceLabel(cheque, tableName, t)}
                    {cheque.customerName ? ` · ${cheque.customerName}` : ""}
                  </span>
                  <span className="truncate text-xs text-[color:var(--text-muted)]">
                    {t((m) => m.olderCheques.closedOn, {
                      date: cheque.closedAt
                        ? (dates.timestamp(cheque.closedAt) ?? "")
                        : dates.businessDate(cheque.businessDate),
                    })}
                  </span>
                </span>
                <span className="shrink-0 text-sm font-extrabold tabular-nums text-[color:var(--warning)]">
                  {t((m) => m.olderCheques.due, {
                    amount: money(cheque.amountDue),
                  })}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {cheques.hasNextPage ? (
        <div className="flex justify-center pt-3">
          <Button
            variant="outline"
            size="sm"
            disabled={cheques.isFetchingNextPage}
            onClick={() => void cheques.fetchNextPage()}
            data-testid="older-cheques-more"
          >
            {cheques.isFetchingNextPage
              ? t((m) => m.olderCheques.loadingMore)
              : t((m) => m.olderCheques.more)}
          </Button>
        </div>
      ) : null}
    </Modal>
  );
}
